import { useMemo } from 'react';
import { Box, Chip, CircularProgress, Typography, useTheme } from '@mui/material';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import {
	selectPaymentCategoryTranslation,
	selectAllFlattenedCategoryOptions,
	getPaymentCategoriesLoading,
} from 'state/reducers/payment-categories.reducer';
import { formatPaymentCategory, createCategoryMap } from 'services/payment-categories.service';
import { FormCard } from 'theme/elements/Forms';

interface IPaymentCategoriesViewProps {
	/** Array of category IDs to display */
	categoryIds: (number | string)[];
	/** Text to show when no categories */
	emptyText?: string;
	/** When true, displays with disabled field styling (green background) */
	disabled?: boolean;
}

/**
 * Read-only component that displays payment categories as a list of chips.
 * Reusable across view/review forms in open-backoffice.
 * Self-contained with FormCard wrapper and title translation.
 */
export default function PaymentCategoriesView({ categoryIds, emptyText, disabled }: IPaymentCategoriesViewProps) {
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const categoryTranslation = useAppSelector(selectPaymentCategoryTranslation);
	// Use all categories (including inactive) for display - data should be fetched by consuming page
	const options = useAppSelector(selectAllFlattenedCategoryOptions);
	const loading = useAppSelector(getPaymentCategoriesLoading);

	// Build option map for quick lookup
	const optionMap = useMemo(() => createCategoryMap(options), [options]);

	// Filter and map category IDs to display items
	const displayCategories = useMemo(() => {
		if (!categoryIds || categoryIds.length === 0) return [];
		return categoryIds
			.map((id) => {
				const strId = String(id);
				const option = optionMap[strId];
				return {
					id: strId,
					label: formatPaymentCategory(option, strId, categoryTranslation),
				};
			})
			.filter((item) => item.label); // Filter out empty labels
	}, [categoryIds, optionMap, categoryTranslation]);

	// Loading state
	if (loading) {
		return (
			<FormCard title={t('page.payment.category.title')}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
					<CircularProgress size={20} />
					<Typography variant="body2" color="text.secondary">
						{t('validations.loading.text')}
					</Typography>
				</Box>
			</FormCard>
		);
	}

	// Empty state
	if (displayCategories.length === 0) {
		return (
			<FormCard title={t('page.payment.category.title')}>
				<Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
					{emptyText || t('page.payment_categories.message.emptyCategories')}
				</Typography>
			</FormCard>
		);
	}

	return (
		<FormCard title={t('page.payment.category.title')}>
			<Box
				sx={{
					display: 'flex',
					flexWrap: 'wrap',
					gap: 1,
					...(disabled && {
						backgroundColor: theme.palette.root.basic6,
						border: `1px solid ${theme.palette.root.mediumGreen}`,
						borderRadius: '8px',
						padding: 1.5,
					}),
				}}
			>
				{displayCategories.map((category) => (
					<Chip
						key={category.id}
						label={category.label}
						variant="outlined"
						disabled={disabled}
						sx={{
							borderColor: disabled ? theme.palette.root.mediumGreen : theme.palette.grey[300],
							backgroundColor: disabled ? 'transparent' : theme.palette.grey[50],
							color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
							fontSize: '0.875rem',
							height: 'auto',
							'& .MuiChip-label': {
								whiteSpace: 'normal',
								padding: '8px 12px',
							},
						}}
					/>
				))}
			</Box>
		</FormCard>
	);
}
